/**
 * <plp-variant-picker> — inline variant picker for PLP product cards.
 *
 * Wraps a product card. On swatch click, swaps the card's image, price,
 * availability text, and "View Product" CTA href to the selected variant.
 *
 * Reads variant data from a sibling <script type="application/json" data-plp-variants>
 * block emitted by card-plp-variant-data.liquid.
 *
 * Progressive enhancement: if JS fails to load, swatch anchors still navigate
 * to /products/<handle>?variant=<id> directly.
 */
class PlpVariantPicker extends HTMLElement {
  constructor() {
    super();
    this._variants = null;
    this._currentId = null;
  }

  connectedCallback() {
    try {
      const dataEl = this.querySelector('script[data-plp-variants]');
      if (!dataEl) return;
      this._variants = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.warn('plp-variant-picker: failed to parse variant data', err);
      return;
    }

    this.addEventListener('click', this._handleSwatchClick.bind(this));
  }

  _handleSwatchClick(event) {
    const swatch = event.target.closest('.plp-swatch:not(.plp-swatch--overflow)');
    if (!swatch) return;

    const variantId = parseInt(swatch.dataset.variantId, 10);
    if (!variantId || !this._variants) return;

    const variant = this._variants.find((v) => v.id === variantId);
    if (!variant) return;

    event.preventDefault();
    this._applyVariant(variant);
    this._setActiveSwatch(swatch);
    this._currentId = variantId;
  }

  _applyVariant(variant) {
    const img = this.querySelector('[data-plp-variant-image]');
    if (img) {
      img.src = variant.image;
    }

    const price = this.querySelector('[data-plp-variant-price]');
    if (price) {
      // Rebuild with safe DOM nodes (textContent) — never innerHTML — so money
      // strings are always treated as text, never markup.
      price.textContent = '';
      const labelled = (wrapEl, labelText, value) => {
        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = labelText;
        wrapEl.appendChild(label);
        wrapEl.appendChild(document.createTextNode(value));
        return wrapEl;
      };
      if (variant.onSale) {
        const sale = document.createElement('span');
        sale.className = 'product-card__price-sale';
        labelled(sale, 'Sale price ', variant.price);

        const compare = document.createElement('s');
        compare.className = 'product-card__price-compare';
        labelled(compare, 'Regular price ', variant.compareAt);

        price.appendChild(sale);
        price.appendChild(document.createTextNode(' '));
        price.appendChild(compare);

        if (variant.savePercent > 0) {
          const badge = document.createElement('span');
          badge.className = 'product-card__price-badge';
          badge.textContent = 'Save ' + variant.savePercent + '%';
          price.appendChild(document.createTextNode(' '));
          price.appendChild(badge);
        }
      } else {
        const regular = document.createElement('span');
        regular.className = 'product-card__price-regular';
        regular.textContent = variant.price;
        price.appendChild(regular);
      }
    }

    const availability = this.querySelector('[data-plp-variant-availability]');
    if (availability) {
      availability.textContent = variant.available ? 'In Stock' : 'Sold Out';
      availability.classList.toggle('product-card__availability--sold-out', !variant.available);
    }

    const cta = this.querySelector('[data-plp-variant-cta]');
    if (cta) {
      cta.href = variant.url;
    }

    const cardLink = this.querySelector('.product-card__link');
    if (cardLink) {
      cardLink.href = variant.url;
    }
  }

  _setActiveSwatch(targetSwatch) {
    this.querySelectorAll('.plp-swatch').forEach((s) => s.classList.remove('is-active'));
    targetSwatch.classList.add('is-active');
  }
}

if (!customElements.get('plp-variant-picker')) {
  customElements.define('plp-variant-picker', PlpVariantPicker);
}
